import React from 'react';
import { Box, Typography, Grid, TextField, Button, Paper } from '@mui/material';

interface EventDetailViewProps {
	event: {
		id: number;
		title: string;
		date: string;
		time: string;
		timezone: string;
		sport?: string | null;
		teams?: string | null;
		why_hot: string;
		broadcast?: string | null;
		city: string;
		country: string;
		zipcode: string;
	};
	onClose: () => void;
	eventType: 'local-event' | 'hot-game';
}

const EventDetailView: React.FC<EventDetailViewProps> = ({ event, onClose, eventType }) => {
	const isLocalEvent = eventType === 'local-event';
	const headerTitle = isLocalEvent ? 'Local Event Details' : 'Hot Game Details';

	// Format date to be more readable
	const formattedDate = event.date ? new Date(event.date).toLocaleDateString('en-US', {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	}) : 'N/A';

	return (
		<Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				{/* Header */}
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={onClose}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{headerTitle}
					</Typography>
					<Box sx={{ width: '120px' }} />
				</Box>

				{/* Event Details */}
				<Box sx={{ p: 4 }}>
					<Typography variant="h6" gutterBottom>
						Event Information
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						View complete event details
					</Typography>

					<Grid container spacing={3}>
						{/* Event Title */}
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Title"
								value={event.title || 'N/A'}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

						{/* Teams (for games) */}
						{event.teams && (
							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Teams"
									value={event.teams}
									InputProps={{ readOnly: true }}
								/>
							</Grid>
						)}

						{/* Sport */}
						{event.sport && (
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Sport"
									value={event.sport}
									InputProps={{ readOnly: true }}
								/>
							</Grid>
						)}

						{/* Broadcast */}
						{event.broadcast && (
							<Grid item xs={12} sm={event.sport ? 6 : 12}>
								<TextField
									fullWidth
									label="Broadcast"
									value={event.broadcast}
									InputProps={{ readOnly: true }}
								/>
							</Grid>
						)}

						{/* Date */}
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Date"
								value={formattedDate}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

						{/* Time */}
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Time"
								value={`${event.time} ${event.timezone}`}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

						{/* Why Hot */}
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={3}
								label="Why It's Hot"
								value={event.why_hot || 'N/A'}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

						{/* Location */}
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="City"
								value={event.city || 'N/A'}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Country"
								value={event.country || 'N/A'}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

						{/* Zipcode */}
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Zipcode"
								value={event.zipcode || 'N/A'}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Paper>
	);
};

export default EventDetailView;
